/**
 * Algolia Search - Main Entry Point
 * Orchestrates search initialization and flow
 *
 * This is a fully independent module that loads separately from the main site JS.
 * It is included via <script type="module"> only on search pages.
 */

// ============================================================
// 1. Foundation (no dependencies)
// ============================================================
import {
  SEARCH_COPY,
  SELECTORS,
  CLASSES,
  STATE_STORAGE_KEY,
  TIER_SEEDS,
} from "./core/constants.js";
import { elements, initElements } from "./core/elements.js";
import {
  state,
  saveToLocalStorage,
  loadFromLocalStorage,
} from "./core/state.js";

// ============================================================
// 2. Utils (no dependencies)
// ============================================================
import * as DateUtils from "./utils/date-utils.js";
import * as StringUtils from "./utils/string-utils.js";
import * as BrandUtils from "./utils/brand-utils.js";
import * as FacetUtils from "./utils/facet-utils.js";
import * as SportUtils from "./utils/sport-utils.js";

// ============================================================
// 3. Services
// ============================================================
import { getClient, getIndex } from "./algolia/client.js";
import * as LocationComparison from "./decision-engine/location-comparison.js";
import * as UserGeo from "./decision-engine/user-geo.js";
import * as CopyTemplates from "./decision-engine/copy-templates.js";
import { encodeTier, decodeTier } from "./url/tier-encoding.js";

// ============================================================
// 4. Filters & Geo
// ============================================================
import * as GenderFilter from "./filters/gender-filter.js";
import * as DateFilter from "./filters/date-filter.js";
import * as GeoParams from "./filters/geo-params.js";
import * as PlanBuilder from "./decision-engine/plan-builder.js";
import * as FilterBuilder from "./filters/filter-builder.js";

// ============================================================
// 5. UI
// ============================================================
import { toggleSkeleton } from "./ui/skeleton.js";
import { renderCampCard } from "./ui/camp-card.js";
import { renderLoadMoreButton } from "./ui/load-more.js";
import * as BannerManager from "./ui/banner-manager.js";
import * as FilterUI from "./ui/filter-ui.js";
import { renderResults } from "./ui/results-renderer.js";

// ============================================================
// 6. API & Search
// ============================================================
import { searchCampByTitle } from "./algolia/camp-title-search.js";
import * as FacetsFetcher from "./algolia/facets-fetcher.js";
import { performSearch } from "./algolia/search-executor.js";
import {
  decideSearchPresentation,
  runDecisionFlow,
} from "./decision-engine/index.js";
import * as DualSearch from "./algolia/dual-search.js";

// ============================================================
// 7. Geocoding & Analytics
// ============================================================
import { handleGeocoding } from "./geocoding/geocode-handler.js";
import * as AlgoliaTracking from "./analytics/algolia-tracking.js";
import * as GTMEvents from "./analytics/gtm-events.js";

// ============================================================
// 8. URL & Events
// ============================================================
import { updateURL } from "./url/url-updater.js";
import { initFromURL, applyCoordinatesFromCache, syncUIFromState } from "./url/url-parser.js";
import { isEmbeddedMode, initEmbeddedMode } from "./modes/embedded.js";
import { setupPopupCloseHandlers } from "./events/popup-handlers.js";
import * as FilterHandlers from "./events/filter-handlers.js";
import { setupEventListeners, setDependencies as setEventSetupDeps } from "./events/event-setup.js";

// ============================================================
// 9. Export modules to window (for debugging/backwards compat)
// ============================================================
/**
 * Re-run camp title search with current filters (used when filters change during title search mode)
 */
async function performTitleSearch() {
  console.log("🔍 [TITLE SEARCH] Re-searching by title with updated filters:", state.zip);
  toggleSkeleton(true);

  try {
    const campSearchResult = await searchCampByTitle(state.zip, {
      sportSlug: state.sportSlug,
      applyAllFilters: true,
    });

    if (campSearchResult?.hits?.length > 0) {
      console.log(`✅ [TITLE SEARCH] Filter update found ${campSearchResult.hits.length} camps`);

      let hits = campSearchResult.hits;

      if (campSearchResult.firstCamp) {
        state.coordinates = {
          lat: campSearchResult.firstCamp.lat,
          lng: campSearchResult.firstCamp.lng,
        };
      }

      state.geocodeResult = {
        rawInput: state.zip,
        recognized: true,
        isTitleSearch: true,
      };

      state.foundCampObjectID = null;
      state.dualSearchLocation = null;
      state.implicitSportSlug = null;
      state.showCorrectedLocationBanner = false;
      state.showUnrecognizedLocationBanner = false;

      const totalCount = campSearchResult.nbHits || hits.length;

      state.allFilteredHits = hits;
      state.totalHits = totalCount;
      state.displayedHits = state.hitsPerPage;

      // Update facets from results
      const facets = { sportSlug: {}, types: {}, gender: {}, brands: {}, skills: {}, live: {} };
      hits.forEach((hit) => {
        if (hit.sportSlug) facets.sportSlug[hit.sportSlug] = (facets.sportSlug[hit.sportSlug] || 0) + 1;
        if (hit.feeCodes && Array.isArray(hit.feeCodes)) hit.feeCodes.forEach((c) => { facets.types[c] = (facets.types[c] || 0) + 1; });
        if (hit.gender && Array.isArray(hit.gender)) hit.gender.forEach((g) => { facets.gender[g] = (facets.gender[g] || 0) + 1; });
        if (hit.brands && Array.isArray(hit.brands)) hit.brands.forEach((b) => { facets.brands[b] = (facets.brands[b] || 0) + 1; });
        if (hit.skills && Array.isArray(hit.skills)) hit.skills.forEach((s) => { facets.skills[s] = (facets.skills[s] || 0) + 1; });
        if (hit.live !== undefined) { const k = String(hit.live); facets.live[k] = (facets.live[k] || 0) + 1; }
      });
      state.facets = facets;

      // Save as allFacets baseline so filter options don't disappear when filters change
      state.allFacets.feeCodes = FacetUtils.mergeFacetKeys(
        state.allFacets.feeCodes || {},
        facets.types || {}
      );
      state.allFacets.brands = FacetUtils.mergeFacetKeys(
        state.allFacets.brands || {},
        facets.brands || {}
      );
      state.allFacets.skills = FacetUtils.mergeFacetKeys(
        state.allFacets.skills || {},
        facets.skills || {}
      );

      toggleSkeleton(false);

      const hitsToDisplay = hits.slice(0, state.displayedHits);
      renderResults(hitsToDisplay, totalCount, false, null);
      updateURL();
      FilterUI.updateFilterUI();

      console.log("✅ [TITLE SEARCH] Rendered", hitsToDisplay.length, "of", totalCount, "camps (total from Algolia)");
    } else {
      console.log("⚠️ [TITLE SEARCH] No camps match current filters");
      state.allFilteredHits = [];
      state.totalHits = 0;
      toggleSkeleton(false);
      renderResults([], 0, false, null);
      updateURL();
      FilterUI.updateFilterUI();
    }
  } catch (error) {
    console.error("❌ [TITLE SEARCH] Filter search failed:", error);
    toggleSkeleton(false);
  }
}

/**
 * Smart search dispatcher: re-runs title search if in title search mode, otherwise location search.
 */
function performSearchAuto() {
  if (state.geocodeResult?.isTitleSearch) {
    performTitleSearch();
  } else {
    performSearch();
  }
}

// Optionally expose some modules on window for external access
window.AlgoliaSearchModules = {
  state,
  elements,
  performSearch,
  performSearchAuto,
  performDualSearch: DualSearch.performDualSearch,
  clearDualSearchState: DualSearch.clearDualSearchState,
  initFromURL,
  // Add more as needed
};

// ============================================================
// 10. Initialize
// ============================================================

// Static data loading state
let staticDataLoaded = false;

/**
 * Load static location data (cities, zips, states)
 * This is critical for isKnownLocation() and getCoordinatesFromStatic() to work
 * when user navigates directly to search results page via URL
 */
async function loadStaticData() {
  if (staticDataLoaded) {
    return;
  }

  try {
    const [citiesResponse, zipsResponse, statesResponse] = await Promise.all([
      fetch("/themes/v2/js/components/search/json/cities.json"),
      fetch("/themes/v2/js/components/search/json/zips.json"),
      fetch("/themes/v2/js/components/search/json/states.json"),
    ]);

    const cities = await citiesResponse.json();
    const zips = await zipsResponse.json();
    const states = await statesResponse.json();

    // Initialize LocationAutocomplete with data if available
    // Must pass states for isKnownLocation() and getCoordinatesFromStatic() to work with state names
    if (window.LocationAutocomplete) {
      window.LocationAutocomplete.init(cities, zips, states);
    }

    staticDataLoaded = true;
    console.log("✅ Static location data loaded (cities, zips, states)");
  } catch (error) {
    console.error("❌ Error loading static data:", error);
  }
}

/**
 * Initialize Algolia Search
 */
async function init() {
  console.log("🔍 Initializing Algolia Search...");
  console.log(
    `📦 Loaded ${Object.keys(window.AlgoliaSearchModules || {}).length} modules`,
  );

  // Check for required dependencies
  if (!window.CampsUtils) {
    console.error("❌ Missing dependency: window.CampsUtils");
    return;
  }

  if (!window.algoliasearch) {
    console.error("❌ Missing dependency: algoliasearch library");
    return;
  }

  // Initialize DOM elements
  initElements();

  // Ensure geolocation data exists (one-time fetch for first visit)
  if (window.CampsUtils?.ensureGeolocationData) {
    try {
      await window.CampsUtils.ensureGeolocationData();
      console.log("✅ Geolocation check complete");
    } catch (e) {
      console.warn("⚠️ Geolocation fetch failed:", e);
    }
  }

  // Load static location data BEFORE initFromURL
  // This is critical for isKnownLocation() and getCoordinatesFromStatic() to work
  // when user navigates directly to search results page via URL
  await loadStaticData();

  // ============================================================
  // Dependency Injection Setup
  // ============================================================
  // Inject dependencies to avoid circular imports

  // Helper function to initialize form components from URL
  function initializeFormComponents() {
    // Initialize dates selection component if available
    if (window.DetailsSelection?.initFromURL) {
      window.DetailsSelection.initFromURL();
    }
    // Initialize location autocomplete if available
    if (window.LocationAutocomplete?.initFromURL) {
      window.LocationAutocomplete.initFromURL();
    }
  }

  // Inject dependencies into filter-builder
  FilterBuilder.setDependencies({
    getSelectedGender: GenderFilter.getSelectedGender,
  });

  // Show error message in the results container (monolith parity: showError)
  function showError() {
    if (elements.resultsContainer) {
      elements.resultsContainer.innerHTML =
        "<li><p>An error occurred. Please try again.</p></li>";
    }
  }

  // Inject dependencies into search-executor
  // (awaited: initFromURL below reaches performSearch/toggleSkeleton synchronously,
  // so a fire-and-forget .then() would inject too late)
  await import("./algolia/search-executor.js").then((module) => {
    module.setUIDependencies({
      renderResults,
      updateURL,
      showError,
      updateFilterUI: FilterUI.updateFilterUI,
      updateFiltersCount: FilterUI.updateFiltersCount,
      applyPresentation: BannerManager.applyPresentation,
      showCorrectedLocationBannerIfNeeded:
        BannerManager.showCorrectedLocationBannerIfNeeded,
      toggleSkeleton,
      runDecisionFlow,
      hideFallbackBanner: BannerManager.hideFallbackBanner,
      computeShowDistance: GeoParams.computeShowDistance,
    });
  });

  // Inject dependencies into filter-ui
  FilterUI.setDependencies({
    handleFilterChange: FilterHandlers.handleFilterChange,
    performSearchAuto,
  });

  // Inject dependencies into results-renderer
  await import("./ui/results-renderer.js").then((module) => {
    module.setDependencies({
      updateActiveFilters: FilterUI.updateActiveFilters,
      pushGTMViewItemList: window.pushGTMViewItemList,
      selectItemTracking: window.selectItemTracking,
    });
  });

  // Inject dependencies into load-more
  await import("./ui/load-more.js").then((module) => {
    module.setRenderResults(renderResults);
  });

  // Simple debounce for filter changes (300ms)
  let filterDebounceTimer = null;
  function filterDebounce() {
    return new Promise((resolve) => {
      clearTimeout(filterDebounceTimer);
      filterDebounceTimer = setTimeout(resolve, 300);
    });
  }

  // Inject dependencies into filter-handlers
  FilterHandlers.setDependencies({
    performSearchAuto,
    updateFiltersCount: FilterUI.updateFiltersCount,
    filterDebounce,
  });

  // Inject dependencies into url-parser
  // (awaited: initFromURL's static-data branch calls performSearch synchronously —
  // an un-awaited .then() leaves performSearch null and the search silently never runs)
  await import("./url/url-parser.js").then((module) => {
    module.setDependencies({
      performSearch,
      initializeFormComponents,
    });
  });

  // Inject dependencies into dual-search
  DualSearch.setDependencies({
    toggleSkeleton,
    renderCampCard,
    renderResults,
    updateURL,
    updateFilterUI: FilterUI.updateFilterUI,
    updateFiltersCount: FilterUI.updateFiltersCount,
    runDecisionFlow,
  });

  // Inject dependencies into event-setup
  setEventSetupDeps({
    performSearchAuto,
    updateFiltersCount: FilterUI.updateFiltersCount,
  });

  // Setup event listeners
  setupEventListeners();

  // Setup popup close handlers
  setupPopupCloseHandlers();

  // Initialize from URL or embedded config
  if (isEmbeddedMode()) {
    await initEmbeddedMode();
    syncUIFromState();
    // Fix timing bug: injection via import().then() resolves before this line,
    // but search-executor calls updateFiltersCount() before injection completes.
    // Call it here directly so preset filter count shows on initial load.
    FilterUI.updateFiltersCount();
    // No GTM search event in embedded mode
  } else {
    await initFromURL();
    // Send GTM event on results page load
    setTimeout(() => {
      GTMEvents.sendSearchResultsEventOnPageLoad();
    }, 500);
  }

  console.log("✅ Algolia Search initialized");
}

// ============================================================
// 11. Start when DOM is ready
// ============================================================
if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}

console.log("🚀 Algolia Search module loaded (modular architecture)");
